// gobdraw.ts — the per-gob DRAW dispatcher: the client-side render arms of the
// gob classes (UnitGob::Draw Unit.cpp:451, StructGob::Draw/DrawSymbol
// Struct.cpp:440-500, SceneryGob::Draw GameObjects.cpp:817, SurfaceDecalGob::Draw
// :655, AnimGob::Draw :937, + the indicator helpers :1026-1141).
//
// The PROVEN sim stays untouched: these functions read sim-gob PUBLIC state
// (m_wx/m_wy, m_ff, m_ani.GetStrip()/GetFrame(), m_szBitmap, consts) and draw
// through the client art stack — the C++ virtual Draw dispatch becomes a
// GetType()/flag dispatch here, preserving each method's logic verbatim.

import type { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import type { TBitmap } from '../gfx/tbitmap.ts';
import { ksideAttackMoveTint } from '../gfx/tbitmap.ts';
import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { CoordMap } from '../gfx/coords.ts';
import type { PackFileReader } from '../io/packfile.ts';
import { ClientAnimationData } from '../gfx/animdata.ts';
import { GetColor, GetSideColor, iclr } from '../gfx/palette.ts';
import { kstDying, kstMinerSuck } from '../../../sim/src/sm/message.ts';
import { projectileDrawPositions } from './projectile-interp.ts';

// ht.h:6019-6026
export const knLayerSurfaceDecal = 0;
export const knLayerDepthSorted = 1;
export const knLayerSmokeFire = 2;
export const knLayerSymbols = 3;
export const knLayerSelection = 4;
export const knLayerEnd = 4;
export const knLayerMiniMap = -1;

// ht.h gob flags the dispatcher needs
const kfGobSelected = 0x4000;
const kfGobBeingBuilt = 0x10;
const kfGobDrawFlashed = 0x80;
// ht.h unit flags
const kfUnitDrawHealthIndicator = 0x0002; // ht.h:6673
const kfUnitHilighted = 0x0080; // ht.h:6679
const ksideNeutral = 0;
const kcyHealthBar = 4; // ht.h:6565
// ht.h AnimGob flags
const kfAnmSmokeFireLayer = 0x0004; // ht.h:6429
const kfAnmSurfaceDecalLayer = 0x0008; // ht.h:6430

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** The hilight sprite surface drawUnitGob's selection arm drives
 *  (render/sprites.ts AnimSprite — Unit.cpp:501-506). */
export interface AnimSpriteLike {
  SetPosition(x: number, y: number): void;
  SetScale(nScale: number): void;
  CaptureFrame(punt: unknown): void;
  GetBounds(): Rect;
}

/** Client art context: lazy caches over the pack art, keyed the way the C++
 *  loads them (ani name → AnimationData; bitmap name → TBitmap). */
export class GobDrawContext {
  readonly tam: TexAtlasMgr;
  readonly coords: CoordMap;
  /** gsim.m_nMiniMapScale (Simulation.cpp:23 init 1; ht.h:5373-5379 Set/Get) —
   *  presentation state the C++ homes on the sim; written by MiniMapControl::Init. */
  nMiniMapScale = 1;
  /** punt->m_panispr (Unit.cpp:499-507) — the hilight ANIM SPRITE owned by the
   *  host session (render/sprites.ts); null = not hilighted (the C++ NULL arm). */
  hilightSpriteFor: ((punt: unknown) => AnimSpriteLike | null) | null = null;
  /** gsim.GetUpdateCount() — the upgrade-effect flash phase (Builder.cpp:373). */
  getUpdateCount: (() => number) | null = null;
  private pakr: PackFileReader;
  private anidCache = new Map<string, ClientAnimationData | null>();
  private tbmCache = new Map<string, TBitmap | null>();

  constructor(pakr: PackFileReader, tam: TexAtlasMgr, coords: CoordMap) {
    this.pakr = pakr;
    this.tam = tam;
    this.coords = coords;
  }

  anidFor(aniName: string): ClientAnimationData | null {
    let a = this.anidCache.get(aniName);
    if (a === undefined) {
      a = new ClientAnimationData();
      if (!a.Init(this.pakr, this.tam, aniName)) a = null;
      this.anidCache.set(aniName, a);
    }
    return a;
  }

  tbmFor(name: string): TBitmap | null {
    let t = this.tbmCache.get(name);
    if (t === undefined) {
      t = this.tam.CreateTBitmap(name);
      this.tbmCache.set(name, t);
    }
    return t;
  }
}

/** The minimal sim-gob surface the dispatcher reads (sim/src/gob classes). */
export interface SimGobLike {
  GetType(): number;
  GetFlags(): number;
  GetSide?(): number;
  m_wx: number;
  m_wy: number;
}

interface SimUnitLike extends SimGobLike {
  m_ani: { GetStrip(): number; GetFrame(): number };
  m_st: number;
  m_fxHealth: number;
  m_wfUnit: number;
  m_puntc: {
    szAniName: string;
    wrcUIBounds: { left: number; top: number; right: number; bottom: number };
    GetArmorStrength(): number;
  };
  GetSide(): number;
  m_fAttackMoveFlash?: boolean; // TS-only: the flash should render RED (attack-move target)
}

// The flash "side": red (ksideAttackMoveTint) for an attack-move target, else white (-1).
const flashSide = (punt: SimUnitLike): number =>
  punt.m_fAttackMoveFlash ? ksideAttackMoveTint : -1;

const rectFromWorldRect = (
  c: CoordMap,
  wrc: { left: number; top: number; right: number; bottom: number },
): Rect => ({
  left: c.PcFromWc(wrc.left),
  top: c.PcFromWc(wrc.top),
  right: c.PcFromWc(wrc.right),
  bottom: c.PcFromWc(wrc.bottom),
});

// GameObjects.cpp:1026-1046
export function GetHealthColorAndLength(
  nNumerator: number,
  nDenominator: number,
  cxWidth: number,
): { clr: ReturnType<typeof GetColor>; nLength: number } {
  let clr;
  if (nNumerator * 2 > nDenominator) clr = GetColor(iclr.kiclrGreen);
  else if (nNumerator * 4 > nDenominator) clr = GetColor(iclr.kiclrYellow);
  else clr = GetColor(iclr.kiclrRed);
  let nLength = Math.floor(((cxWidth - 2) * nNumerator) / nDenominator);
  if (nLength === 0 && nNumerator > 0) nLength = 1;
  return { clr, nLength };
}

// GameObjects.cpp:1108-1127
export function DrawHealthIndicator(
  pbm: CpuDibBitmap,
  prc: Rect,
  nNumerator: number,
  nDenominator: number,
): void {
  const cxWidth = prc.right - prc.left;
  const { clr, nLength } = GetHealthColorAndLength(nNumerator, nDenominator, cxWidth);
  pbm.Shadow(prc.left, prc.top - kcyHealthBar, cxWidth, kcyHealthBar);
  pbm.Fill(prc.left + 1, prc.top - (kcyHealthBar - 1), nLength, kcyHealthBar - 2, clr);
}

// GameObjects.cpp:1047-1106 — the perf flag is pinned ON (lobby forces kfPerfMax)
export function DrawSelectionIndicator(
  pbm: CpuDibBitmap,
  prc: Rect,
  nNumerator: number,
  nDenominator: number,
): void {
  const cxWidth = prc.right - prc.left;
  const cyHeight = prc.bottom - prc.top;
  const nCornerWidth = Math.floor(cxWidth / 4);
  const nCornerHeight = Math.floor(cyHeight / 4);
  const nThickness = 1;
  const clr = GetColor(iclr.kiclrWhite);

  pbm.Fill(prc.left, prc.top, nCornerWidth, nThickness, clr);
  pbm.Fill(prc.left, prc.top, nThickness, nCornerHeight, clr);
  pbm.Fill(prc.right - nCornerWidth, prc.top, nCornerWidth, nThickness, clr);
  pbm.Fill(prc.right - nThickness, prc.top, nThickness, nCornerHeight, clr);
  pbm.Fill(prc.left, prc.bottom - nThickness, nCornerWidth, nThickness, clr);
  pbm.Fill(prc.left, prc.bottom - nCornerHeight, nThickness, nCornerHeight, clr);
  pbm.Fill(prc.right - nCornerWidth, prc.bottom - nThickness, nCornerWidth, nThickness, clr);
  pbm.Fill(prc.right - nThickness, prc.bottom - nCornerHeight, nThickness, nCornerHeight, clr);

  DrawHealthIndicator(pbm, prc, nNumerator, nDenominator);
}

// GameObjects.cpp:1129-1140
export function DrawBuildProgressIndicator(
  pbm: CpuDibBitmap,
  prc: Rect,
  nNumerator: number,
  nDenominator: number,
): void {
  const clr = GetColor(iclr.kiclrYellow);
  const cxWidth = prc.right - prc.left;
  const nLength = Math.floor(((cxWidth - 2) * nNumerator) / nDenominator);
  const y = prc.top + ((prc.bottom - prc.top) >> 1);
  pbm.Shadow(prc.left, y, cxWidth, 6);
  pbm.Fill(prc.left + 1, y + 1, nLength, 4, clr);
}

// game/Unit.cpp:451-501 — the shared unit/structure draw
export function drawUnitGob(
  ctx: GobDrawContext,
  punt: SimUnitLike,
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  const ff = punt.GetFlags();
  if (
    (nLayer === knLayerDepthSorted && punt.m_st !== kstDying) ||
    (nLayer === knLayerSurfaceDecal && punt.m_st === kstDying)
  ) {
    let side = punt.GetSide();
    if (ff & kfGobDrawFlashed) side = flashSide(punt);
    else if (ff & kfGobBeingBuilt) side = ksideNeutral;

    const xT = ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin;
    const yT = ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin;
    const anid = ctx.anidFor(punt.m_puntc.szAniName);
    if (anid !== null) {
      // side -1 = flash-white (TBitmap.Flash — tbitmap.cpp:58-68)
      anid.DrawFrame(punt.m_ani.GetStrip(), punt.m_ani.GetFrame(), pbm, xT, yT, side);
    }
  } else if (nLayer === knLayerSelection) {
    if (ff & kfGobSelected) {
      const rcT = rectFromWorldRect(ctx.coords, punt.m_puntc.wrcUIBounds);
      offsetRect(
        rcT,
        -xViewOrigin + ctx.coords.PcFromWc(punt.m_wx),
        -yViewOrigin + ctx.coords.PcFromWc(punt.m_wy),
      );
      DrawSelectionIndicator(pbm, rcT, punt.m_fxHealth, punt.m_puntc.GetArmorStrength());
    } else if (punt.m_wfUnit & kfUnitDrawHealthIndicator) {
      const rcT = rectFromWorldRect(ctx.coords, punt.m_puntc.wrcUIBounds);
      offsetRect(
        rcT,
        -xViewOrigin + ctx.coords.PcFromWc(punt.m_wx),
        -yViewOrigin + ctx.coords.PcFromWc(punt.m_wy),
      );
      DrawHealthIndicator(pbm, rcT, punt.m_fxHealth, punt.m_puntc.GetArmorStrength());
    }
    // Unit.cpp:499-507 — the hilight sprite capture (m_panispr): position at
    // the unit, scale so the capture is 4 tiles high, recapture on frame change
    if (punt.m_wfUnit & kfUnitHilighted) {
      const panispr = ctx.hilightSpriteFor?.(punt) ?? null;
      if (panispr !== null) {
        const kwcTile = 256; // ht.h kwcTile (1 << 8)
        const xT2 = ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin;
        const yT2 = ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin;
        panispr.SetPosition(xT2, yT2);
        panispr.SetScale(
          (kwcTile * 4) / (punt.m_puntc.wrcUIBounds.bottom - punt.m_puntc.wrcUIBounds.top),
        );
        panispr.CaptureFrame(punt);
      }
    }
  }
}

function offsetRect(rc: Rect, dx: number, dy: number): void {
  rc.left += dx;
  rc.right += dx;
  rc.top += dy;
  rc.bottom += dy;
}

// ht.h:6671-6677 (word m_wfUnit bits)
const kfUnitDrawRepairingSymbol = 0x0004;
const kfUnitDrawNeedsPowerSymbol = 0x0010;
const kfUnitDrawNeedCreditsSymbol = 0x0020;

// game/Struct.cpp:440-450 — symbol centered over the structure's UI bounds
function drawStructSymbol(
  ctx: GobDrawContext,
  punt: SimUnitLike,
  pszPng: string,
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
): void {
  const ptbm = ctx.tbmFor(pszPng);
  if (ptbm === null) return;
  const x = -xViewOrigin + ctx.coords.PcFromWc(punt.m_wx);
  const y = -yViewOrigin + ctx.coords.PcFromWc(punt.m_wy);
  const rcT = rectFromWorldRect(ctx.coords, punt.m_puntc.wrcUIBounds);
  const sizT = ptbm.GetSize();
  ptbm.BltTo(
    pbm,
    x + Math.trunc((rcT.right - rcT.left - sizT.cx) / 2),
    y + Math.trunc((rcT.bottom - rcT.top - sizT.cy) / 2),
  );
}

// ht.h:5430-5432 — minimap coord from world coord; the shift is "7 for hires,
// 8 for lores" (nMiniMapScale 2 / 1)
export function MmcFromWc(wc: number, nMiniMapScale: number): number {
  return ((wc + 0x007f) & 0xff80) >> (9 - nMiniMapScale);
}

/** The per-gob minimap draw dispatcher (Gob::Draw with nLayer=knLayerMiniMap).
 *  Only StructGob carries a knLayerMiniMap arm (game/Struct.cpp:455-468):
 *  side-colored (kiclrWhite when selected) tile-footprint fill at the minimap
 *  scale. UnitGob::Draw (game/Unit.cpp:451) has NO knLayerMiniMap arm — every
 *  other gob falls through both layer tests and draws nothing; the shipped
 *  minimap paints non-structure units inline in MiniMapControl::RedrawTRect
 *  (game/SimUI.cpp:3041-3056). */
export function drawGobMiniMap(
  ctx: GobDrawContext,
  pgob: SimGobLike & { m_puntc?: { ctxTiles?: number; ctyTiles?: number } },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
): void {
  if (!(pgob.GetFlags() & 0x400 /* kfGobStructure */)) return;
  // game/Struct.cpp:455-468
  let clr;
  if (pgob.GetFlags() & kfGobSelected) {
    clr = GetColor(iclr.kiclrWhite);
  } else {
    clr = GetSideColor(pgob.GetSide!());
  }

  const cxy = ctx.nMiniMapScale; // gsim.GetMiniMapScale()
  pbm.Fill(
    xViewOrigin + MmcFromWc(pgob.m_wx, ctx.nMiniMapScale),
    yViewOrigin + MmcFromWc(pgob.m_wy, ctx.nMiniMapScale),
    (pgob.m_puntc?.ctxTiles ?? 0) * cxy,
    (pgob.m_puntc?.ctyTiles ?? 0) * cxy,
    clr,
  );
}

// game/Struct.cpp:452-500 — the StructGob::Draw override: the minimap fill,
// status symbols + the being-built health bar (build progress shows as rising
// health), then the shared UnitGob arms.
export function drawStructGob(
  ctx: GobDrawContext,
  punt: SimUnitLike,
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  const ff = punt.GetFlags();
  if (nLayer === knLayerMiniMap) {
    drawGobMiniMap(ctx, punt as never, pbm, xViewOrigin, yViewOrigin);
    return;
  }
  if (nLayer === knLayerSymbols) {
    // UnitGob::Draw is a no-op on this layer; only one symbol shows at a time —
    // low power wins over repairing / need-credits (mutually exclusive pair)
    if (punt.m_wfUnit & kfUnitDrawNeedsPowerSymbol) {
      drawStructSymbol(ctx, punt, 'needs_power_symbol.png', pbm, xViewOrigin, yViewOrigin);
    } else if (punt.m_wfUnit & kfUnitDrawRepairingSymbol) {
      drawStructSymbol(ctx, punt, 'repairing_symbol.png', pbm, xViewOrigin, yViewOrigin);
    } else if (punt.m_wfUnit & kfUnitDrawNeedCreditsSymbol) {
      drawStructSymbol(ctx, punt, 'needs_credits_symbol.png', pbm, xViewOrigin, yViewOrigin);
    }
    return;
  }
  if (nLayer === knLayerSelection && (ff & (kfGobSelected | kfGobBeingBuilt)) === kfGobBeingBuilt) {
    const rcT = rectFromWorldRect(ctx.coords, punt.m_puntc.wrcUIBounds);
    offsetRect(
      rcT,
      -xViewOrigin + ctx.coords.PcFromWc(punt.m_wx),
      -yViewOrigin + ctx.coords.PcFromWc(punt.m_wy),
    );
    DrawHealthIndicator(pbm, rcT, punt.m_fxHealth, punt.m_puntc.GetArmorStrength());
    return;
  }
  drawUnitGob(ctx, punt, pbm, xViewOrigin, yViewOrigin, nLayer);
}

const kfGobActive = 0x200; // ht.h:5998
const kfGobAnimationChanged = 0x80000; // ht.h:6008
const kwcFullnessPip = 32; // ht.h:6567 WcFromTile16ths(2)

// game/GameObjects.cpp:1141-1167 — the galaxite-pink pip row under a fillable gob
export function DrawFullnessIndicator(
  ctx: GobDrawContext,
  pbm: CpuDibBitmap,
  prc: Rect,
  nPips: number,
  nPipsMax: number,
): void {
  if (nPips === 0) return;
  if (nPips > nPipsMax) nPips = nPipsMax;
  const clr = GetColor(iclr.kiclrFullnessIndicator);
  const cxyPip = ctx.coords.PcFromWc(kwcFullnessPip);
  const cx = nPipsMax * (cxyPip + 1) + 1;
  let x = prc.left + Math.trunc((prc.right - prc.left - cx) / 2);
  const y = prc.bottom + 1;
  pbm.Shadow(x, y - 1, cx, cxyPip + 2); // kcyFullnessBar (ht.h:6569)
  x++;
  for (let i = 0; i < nPips; i++, x += cxyPip + 1) {
    pbm.Fill(x, y, cxyPip, cxyPip, clr);
  }
}

// game/Warehouse.cpp:58-93 — WarehouseGob::Draw. QUIRK (sim-quirks B-series):
// the C++ DRAW sets the sim animation frame from the player's live
// credits/capacity (Gob::SetAnimationFrame) — presentation MUTATES saved sim
// state. The TS renderer reproduces the mutation exactly so post-draw saves
// stay byte-identical with the real client.
/** The galaxite-storage pip count derived from the LIVE player credits/capacity —
 *  UNCLAMPED (the warehouse fill art clamps to 10 at its call site; the processor
 *  overlay's C++-parity draw does not). Shared with SimUIForm.OnUpdateMapInvalidate's
 *  per-paint value diff: credits change WITHOUT any gob-side damage, so under scoped
 *  invalidation the invalidate pass re-marks credits-derived gobs when THIS moves. */
export function creditsPips(pplr: { GetCredits(): number; GetCapacity(): number }): number {
  const nCapacity = pplr.GetCapacity();
  if (nCapacity === 0) return 0;
  return Math.trunc((pplr.GetCredits() * 10 + Math.trunc(nCapacity / 20)) / nCapacity);
}

export function drawWarehouseGob(
  ctx: GobDrawContext,
  punt: SimUnitLike & {
    m_ani: SimUnitLike['m_ani'] & { m_nFrame: number };
    m_pplr: { GetCredits(): number; GetCapacity(): number };
    SetFlags(ff: number): void;
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  const ff = punt.GetFlags();
  if (!(ff & kfGobActive)) {
    drawStructGob(ctx, punt, pbm, xViewOrigin, yViewOrigin, nLayer);
    return;
  }
  let nPips = creditsPips(punt.m_pplr);
  if (nPips > 10) nPips = 10; // over capacity!
  // Gob::SetAnimationFrame (GameObjects.cpp:359-364): 0,1,2=empty 3,4,5=half
  // 6,7,8=mostly 9,10=full
  punt.m_ani.m_nFrame = Math.trunc(nPips / 3);
  punt.SetFlags((ff | kfGobAnimationChanged) >>> 0);

  drawStructGob(ctx, punt, pbm, xViewOrigin, yViewOrigin, nLayer);

  if (nLayer === knLayerSelection && ff & kfGobSelected) {
    // UnitGob::GetUIBounds (Unit.cpp:566): world bounds at the gob position
    const rcT = rectFromWorldRect(ctx.coords, {
      left: punt.m_wx + punt.m_puntc.wrcUIBounds.left,
      top: punt.m_wy + punt.m_puntc.wrcUIBounds.top,
      right: punt.m_wx + punt.m_puntc.wrcUIBounds.right,
      bottom: punt.m_wy + punt.m_puntc.wrcUIBounds.bottom,
    });
    offsetRect(rcT, -xViewOrigin, -yViewOrigin);
    DrawFullnessIndicator(ctx, pbm, rcT, nPips, 10);
  }
}

const kfHcapShowEnemyBuildProgress = 0x0010; // ht.h:291

// game/Builder.cpp:314-329 + :343-356 — the build-progress bar over builders.
// Needs the LOCAL player (ally/handicap gating) — client-presentation state.
export function drawBuilderProgress(
  ctx: GobDrawContext,
  pgob: SimUnitLike & {
    m_bq: { IsEmpty(): boolean; Peek(): number };
    m_fxHealthBuilding: number;
    IsAlly(side: number): boolean;
  },
  plrLocal: { GetSide(): number; GetHandicap(): number },
  apuntc: { GetArmorStrength(): number }[],
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (nLayer !== knLayerDepthSorted) return;
  if (pgob.m_bq.IsEmpty()) return;
  const show =
    pgob.IsAlly(plrLocal.GetSide()) ||
    (plrLocal.GetHandicap() & kfHcapShowEnemyBuildProgress) !== 0;
  if (!show) return;
  const rcT = rectFromWorldRect(ctx.coords, pgob.m_puntc.wrcUIBounds);
  offsetRect(
    rcT,
    -xViewOrigin + ctx.coords.PcFromWc(pgob.m_wx),
    -yViewOrigin + ctx.coords.PcFromWc(pgob.m_wy),
  );
  DrawBuildProgressIndicator(
    pbm,
    rcT,
    pgob.m_fxHealthBuilding,
    apuntc[pgob.m_bq.Peek()].GetArmorStrength(),
  );
}

/** game/Tank.cpp:65-98 — tanks REPLACE the depth-sorted arm: base + turret at
 *  the base's current special point (and draw even while dying — the death anim
 *  plays through the same strips); other layers fall to the shared unit arm. */
export function drawTankGob(
  ctx: GobDrawContext,
  punt: SimUnitLike & {
    m_aniTurret: { GetStrip(): number; GetFrame(): number };
    m_ani: SimUnitLike['m_ani'] & { GetSpecialPoint(nFrame?: number): { x: number; y: number } };
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (nLayer === knLayerDepthSorted) {
    let side = punt.GetSide();
    if (punt.GetFlags() & kfGobDrawFlashed) side = flashSide(punt);
    const anid = ctx.anidFor(punt.m_puntc.szAniName);
    if (anid === null) return;
    const x = ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin;
    const y = ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin;
    anid.DrawFrame(punt.m_ani.GetStrip(), punt.m_ani.GetFrame(), pbm, x, y, side);
    const pt = punt.m_ani.GetSpecialPoint();
    anid.DrawFrame(
      punt.m_aniTurret.GetStrip(),
      punt.m_aniTurret.GetFrame(),
      pbm,
      x + pt.x,
      y + pt.y,
      side,
    );
  } else {
    drawUnitGob(ctx, punt, pbm, xViewOrigin, yViewOrigin, nLayer);
  }
}

/** game/Tower.cpp:92-119 — the turret OVERLAY after the struct base draw,
 *  skipped when the base shows the destroyed strip (2). */
export function drawTowerTurret(
  ctx: GobDrawContext,
  punt: SimUnitLike & {
    m_aniTurret: { GetStrip(): number; GetFrame(): number };
    m_ani: SimUnitLike['m_ani'] & { GetSpecialPoint(nFrame?: number): { x: number; y: number } };
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (nLayer !== knLayerDepthSorted) return;
  if (punt.m_ani.GetStrip() === 2) return;
  let side = punt.GetSide();
  const ff = punt.GetFlags();
  if (ff & kfGobDrawFlashed) side = flashSide(punt);
  else if (ff & kfGobBeingBuilt) side = ksideNeutral;
  const anid = ctx.anidFor(punt.m_puntc.szAniName);
  if (anid === null) return;
  const pt = punt.m_ani.GetSpecialPoint();
  anid.DrawFrame(
    punt.m_aniTurret.GetStrip(),
    punt.m_aniTurret.GetFrame(),
    pbm,
    ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin + pt.x,
    ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin + pt.y,
    side,
  );
}

/** game/Processor.cpp:150-184 — after the struct base: the docked FAKE MINER
 *  (strip 4, frame by approach side, at m_wptFakeMiner — drawn with the RAW side,
 *  before the flash/built overrides) then the processing overlay animation. */
export function drawProcessorOverlay(
  ctx: GobDrawContext,
  punt: SimUnitLike & {
    m_aniOverlay: { GetStrip(): number; GetFrame(): number };
    m_wptFakeMiner: { wx: number; wy: number };
    m_pplr: { GetCredits(): number; GetCapacity(): number };
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  // Processor.cpp:184-197 — the SELECTION layer draws the owner's stored-
  // galaxite pips below a selected processor. FOUND BY THE F2 FORMS ORACLE
  // (2026-06-13): the TS renderer skipped this arm — a selected processor
  // showed no fullness row (38px diff vs the real client).
  if (nLayer === knLayerSelection && punt.GetFlags() & kfGobSelected) {
    const rcT = rectFromWorldRect(ctx.coords, {
      left: punt.m_wx + punt.m_puntc.wrcUIBounds.left,
      top: punt.m_wy + punt.m_puntc.wrcUIBounds.top,
      right: punt.m_wx + punt.m_puntc.wrcUIBounds.right,
      bottom: punt.m_wy + punt.m_puntc.wrcUIBounds.bottom,
    });
    offsetRect(rcT, -xViewOrigin, -yViewOrigin);
    // UNCLAMPED (C++ parity — the warehouse arm clamps, this one never did)
    DrawFullnessIndicator(ctx, pbm, rcT, creditsPips(punt.m_pplr), 10);
    return;
  }
  if (nLayer !== knLayerDepthSorted) return;
  if (punt.m_ani.GetStrip() === 2) return; // destroyed base
  let side = punt.GetSide();
  const anid = ctx.anidFor(punt.m_puntc.szAniName);
  if (anid === null) return;

  const kwxInvalid = -1000; // ht.h:6993
  if (punt.m_wptFakeMiner.wx !== kwxInvalid) {
    const kwcTile = 256;
    const ifrm = punt.m_wptFakeMiner.wy < punt.m_wy + kwcTile ? 1 : 0;
    anid.DrawFrame(
      4,
      ifrm,
      pbm,
      ctx.coords.PcFromUwc(punt.m_wptFakeMiner.wx) - xViewOrigin,
      ctx.coords.PcFromWc(punt.m_wptFakeMiner.wy) - yViewOrigin,
      side,
    );
  }

  const ff = punt.GetFlags();
  if (ff & kfGobDrawFlashed) side = flashSide(punt);
  else if (ff & kfGobBeingBuilt) side = ksideNeutral;
  anid.DrawFrame(
    punt.m_aniOverlay.GetStrip(),
    punt.m_aniOverlay.GetFrame(),
    pbm,
    ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin,
    ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin,
    side,
  );
}

/** game/Miner.cpp:150-159 — the vacuum overlay while sucking: the 16-dir body
 *  strip maps to the 8-strip vacuum (strip/2; the C++ sets it AT DRAW).
 *
 *  MUST go through Gob::SetAnimationStrip (GameObjects.cpp:352-357), which sets
 *  kfGobAnimationChanged on the GOB — a genuine C++ presentation->sim feedback:
 *  the next suck-update AdvanceAnimation(m_aniVacuum) then advances by 1 instead
 *  of GetUpdateCount(), which sets the miner's SP UpdateInterval cadence (the
 *  suck tick lands every 8 updates only because the client paints between every
 *  pair of updates). Calling Animation.SetStrip directly desynced the TS sim's
 *  suck cadence from the real client by 1-2 updates (live-frame state gate,
 *  melee-4p-short-u1099 w1+). */
export function drawMinerVacuum(
  ctx: GobDrawContext,
  punt: SimUnitLike & {
    m_aniVacuum: { GetStrip(): number; GetFrame(): number; SetStrip(n: number): void };
    m_fHidden?: boolean;
    m_nGalaxiteAmount?: number;
    SetAnimationStrip(pani: unknown, nStrip: number): void;
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (punt.m_fHidden) return;
  // Miner.cpp:159-164 — the SELECTION layer draws the carried-galaxite pips
  // below a selected miner (knGalaxiteValue=10, knMinerGalaxiteMax=100 —
  // sim/src/gob/miner.ts:39-40). FOUND BY THE F2 FORMS ORACLE (2026-06-13):
  // the TS renderer skipped this arm, like the processor's.
  if (nLayer === knLayerSelection && punt.GetFlags() & kfGobSelected) {
    const rcT = rectFromWorldRect(ctx.coords, punt.m_puntc.wrcUIBounds);
    offsetRect(
      rcT,
      -xViewOrigin + ctx.coords.PcFromUwc(punt.m_wx),
      -yViewOrigin + ctx.coords.PcFromUwc(punt.m_wy),
    );
    DrawFullnessIndicator(
      ctx,
      pbm,
      rcT,
      Math.trunc(Math.trunc((punt.m_nGalaxiteAmount ?? 0) / 10) / 2),
      Math.trunc(Math.trunc(100 / 10) / 2),
    );
    return;
  }
  if (punt.m_st !== kstMinerSuck || nLayer !== knLayerDepthSorted) return;
  punt.SetAnimationStrip(punt.m_aniVacuum, punt.m_ani.GetStrip() >> 1);
  const anid = ctx.anidFor('vacuum.anir');
  if (anid === null) return;
  anid.DrawFrame(
    punt.m_aniVacuum.GetStrip(),
    punt.m_aniVacuum.GetFrame(),
    pbm,
    ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin,
    ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin,
    punt.GetSide(),
  );
}

/** game/Replicator.cpp:140-183 — the replicator's multi-pass draw: two quadrant
 *  strips by enabled-state (frame pinned 0; the SDL y-1 seam hack on the second
 *  pass) + the lights strip while replicating. The strip/frame writes MUTATE the
 *  sim's m_ani exactly as the C++ does (sim-quirks B21 — those bytes are masked
 *  in the savegame oracle for replicators). */
export function drawReplicatorGob(
  ctx: GobDrawContext,
  punt: SimUnitLike & {
    m_ani: SimUnitLike['m_ani'] & { SetStrip(n: number): void; SetFrame?(n: number): void };
    m_fEnabled: boolean;
    m_fReplicating: boolean;
    m_ifrmLights: number;
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (nLayer === knLayerDepthSorted) {
    const x = ctx.coords.PcFromUwc(punt.m_wx) - xViewOrigin;
    const y = ctx.coords.PcFromUwc(punt.m_wy) - yViewOrigin;
    let side = punt.GetSide();
    if (punt.GetFlags() & kfGobDrawFlashed) side = flashSide(punt);
    const anid = ctx.anidFor(punt.m_puntc.szAniName);
    if (anid === null) return;

    const ani = punt.m_ani as unknown as { SetStrip(n: number): void; m_nFrame: number };
    (ani as unknown as { m_nFrame: number }).m_nFrame = 0; // SetFrame(0)
    ani.SetStrip(punt.m_fEnabled ? 2 : 0);
    anid.DrawFrame(punt.m_ani.GetStrip(), punt.m_ani.GetFrame(), pbm, x, y, side);
    ani.SetStrip(punt.m_fEnabled ? 3 : 1);
    anid.DrawFrame(punt.m_ani.GetStrip(), punt.m_ani.GetFrame(), pbm, x, y - 1, side); // SDL seam hack
    if (punt.m_fReplicating) {
      ani.SetStrip(anid.GetStripIndex('l 0'));
      (ani as unknown as { m_nFrame: number }).m_nFrame = (punt.m_ifrmLights / 2) | 0;
      anid.DrawFrame(punt.m_ani.GetStrip(), punt.m_ani.GetFrame(), pbm, x, y, side);
    }
  } else if (nLayer === knLayerSelection) {
    if (punt.GetFlags() & kfGobSelected) {
      const rcT = rectFromWorldRect(ctx.coords, punt.m_puntc.wrcUIBounds);
      offsetRect(
        rcT,
        -xViewOrigin + ctx.coords.PcFromWc(punt.m_wx),
        -yViewOrigin + ctx.coords.PcFromWc(punt.m_wy),
      );
      DrawSelectionIndicator(pbm, rcT, punt.m_fxHealth, punt.m_puntc.GetArmorStrength());
    }
  } else {
    // Replicator.cpp:185 — all other layers fall to StructGob::Draw
    drawStructGob(ctx, punt, pbm, xViewOrigin, yViewOrigin, nLayer);
  }
}

// game/GameObjects.cpp:817-832
export function drawSceneryGob(
  ctx: GobDrawContext,
  pgob: SimGobLike & { m_szBitmap: string },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (nLayer !== knLayerDepthSorted) return;
  const ptbm = ctx.tbmFor(pgob.m_szBitmap);
  if (ptbm === null) return;
  const x = ctx.coords.PcFromUwc(pgob.m_wx) - xViewOrigin;
  const y = ctx.coords.PcFromUwc(pgob.m_wy) - yViewOrigin;
  ptbm.BltTo(pbm, x, y, pgob.GetSide?.() ?? ksideNeutral);
}

// game/GameObjects.cpp:655-671 — origin = center column / bottom row
// game/Unit.cpp:192-195 gaptbmScorches — ScorchGob's m_ptbm by its saved m_nScorch
// (ScorchGob::LoadState, GameObjects.cpp:703)
const SCORCH_NAMES = ['scorch_8x8.png', 'scorch_16x16.png', 'scorch_32x16.png', 'scorch_48x48.png'];

/** The decal's m_ptbm bitmap NAME: scorches resolve through gaptbmScorches by
 *  m_nScorch; plain decals carry the name — shared by the draw and the
 *  GetClippingBounds equivalent (simdraw.ts gobClippingBounds). */
export function surfaceDecalBitmapName(pgob: {
  m_szBitmap: string | null;
  m_nScorch?: number;
}): string | null {
  return pgob.m_nScorch !== undefined ? (SCORCH_NAMES[pgob.m_nScorch] ?? null) : pgob.m_szBitmap;
}

export function drawSurfaceDecalGob(
  ctx: GobDrawContext,
  pgob: SimGobLike & { m_szBitmap: string | null; m_nScorch?: number },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  if (nLayer !== knLayerSurfaceDecal) return;
  const szBitmap = surfaceDecalBitmapName(pgob);
  if (szBitmap === null || szBitmap === undefined) return;
  const ptbm = ctx.tbmFor(szBitmap);
  if (ptbm === null) return;
  const siz = ptbm.GetSize();
  const x = ctx.coords.PcFromUwc(pgob.m_wx) - xViewOrigin - ((siz.cx - 1) >> 1);
  const y = ctx.coords.PcFromUwc(pgob.m_wy) - yViewOrigin - (siz.cy - 1);
  ptbm.BltTo(pbm, x, y, pgob.GetSide?.() ?? ksideNeutral);
}

// game/GameObjects.cpp:937-951
export function drawAnimGob(
  ctx: GobDrawContext,
  pgob: SimGobLike & {
    m_wfAnm: number;
    m_ani: { GetStrip(): number; GetFrame(): number; GetData(): { name: string } | null };
  },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  let nLayerDesignated: number;
  if (pgob.m_wfAnm & kfAnmSmokeFireLayer) nLayerDesignated = knLayerSmokeFire;
  else if (pgob.m_wfAnm & kfAnmSurfaceDecalLayer) nLayerDesignated = knLayerSurfaceDecal;
  else nLayerDesignated = knLayerDepthSorted;
  if (nLayer !== nLayerDesignated) return;
  const data = pgob.m_ani.GetData();
  if (data === null) return;
  const anid = ctx.anidFor(`${data.name}.anir`);
  if (anid === null) return;
  anid.DrawFrame(
    pgob.m_ani.GetStrip(),
    pgob.m_ani.GetFrame(),
    pbm,
    ctx.coords.PcFromUwc(pgob.m_wx) - xViewOrigin,
    ctx.coords.PcFromUwc(pgob.m_wy) - yViewOrigin,
    pgob.GetSide?.() ?? ksideNeutral,
  );
}

// ── the shipped-web render arms stamped in the M7 ledger triage ─────────────

// perf options (ht.h kfPerf*) — the render gates. The TS client pins them ON
// (kfPerfMax, as the MP lobby forces and the SP default ships); the options
// form mutates its own copy (gameoptions.ts) — wiring a live mask through the
// draw context is deliberate-future (matches kfPerfSelectionBrackets above).
const kfPerfRocketShots = 0x0001; // ht.h (GameOptions.cpp kidcRocketShots)
const kfPerfRocketImpacts = 0x0004;
const kfPerfShots = 0x0008;
const kfPerfShotImpacts = 0x0010;
const gwfPerfOptions = 0xffff; // kfPerfAll — pinned (see note above)
// Cosmetic after-image: the trailing echo sprite(s) draw at this opacity (0-255). ~50%.
const kProjectileEchoOpacity = 128;

interface ShotGobLike extends SimGobLike {
  GetId(): number;
  m_ani: { GetStrip(): number; GetFrame(): number; GetData(): { name: string } | null };
}

/** game/Tank.cpp:733-767 (TankShotGob — inherited by Bullet/ArtilleryShot/
 *  AndyShot) + game/LRInfantry.cpp:268-301 (RocketGob): the smoke-fire-layer
 *  projectile draw. Strip 0 = travel, strip 1 = impact; each side of the split
 *  has its own perf gate (rockets use the kfPerfRocket* pair). */
export function drawShotGob(
  ctx: GobDrawContext,
  pgob: ShotGobLike,
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
  fRocket: boolean,
): void {
  if (nLayer !== knLayerSmokeFire) return;
  if (pgob.m_ani.GetStrip() === 0) {
    if (!(gwfPerfOptions & (fRocket ? kfPerfRocketShots : kfPerfShots))) return;
  } else {
    if (!(gwfPerfOptions & (fRocket ? kfPerfRocketImpacts : kfPerfShotImpacts))) return;
  }
  const data = pgob.m_ani.GetData();
  if (data === null) return;
  const anid = ctx.anidFor(`${data.name}.anir`);
  if (anid === null) return;
  // COSMETIC: draw the interp glide position(s) — the after-image echoes then the lead
  // (back-to-front), or [sim pos] when not tracked. MUST match shotClippingBounds (same
  // lookup) so the damage rect covers every blit — no smear. See projectile-interp.ts.
  const positions = projectileDrawPositions(pgob) ?? [{ wx: pgob.m_wx, wy: pgob.m_wy }];
  const strip = pgob.m_ani.GetStrip(),
    frame = pgob.m_ani.GetFrame();
  const side = pgob.GetSide?.() ?? ksideNeutral;
  const lead = positions.length - 1; // last = current; earlier = trailing after-image echoes
  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    anid.DrawFrame(
      strip,
      frame,
      pbm,
      ctx.coords.PcFromUwc(p.wx) - xViewOrigin,
      ctx.coords.PcFromUwc(p.wy) - yViewOrigin,
      side,
      null,
      i === lead ? 255 : kProjectileEchoOpacity,
    ); // echoes are semitransparent
  }
}

/** TankShotGob/RocketGob::GetClippingBounds (Tank.cpp:733-751,
 *  LRInfantry.cpp:268-285): the perf gates empty the rect entirely. */
export function shotClippingBounds(ctx: GobDrawContext, pgob: ShotGobLike, fRocket: boolean): Rect {
  const empty = { left: 0, top: 0, right: 0, bottom: 0 };
  if (pgob.m_ani.GetStrip() === 0) {
    if (!(gwfPerfOptions & (fRocket ? kfPerfRocketShots : kfPerfShots))) return empty;
  } else {
    if (!(gwfPerfOptions & (fRocket ? kfPerfRocketImpacts : kfPerfShotImpacts))) return empty;
  }
  const data = pgob.m_ani.GetData();
  const anid = data === null ? null : ctx.anidFor(`${data.name}.anir`);
  if (anid === null) return empty;
  // COSMETIC: damage the UNION of every interp draw position (echoes + lead — the same
  // lookup drawShotGob uses) so gobInvalidate's old+new damage covers the whole trail.
  const strip = pgob.m_ani.GetStrip(),
    frame = pgob.m_ani.GetFrame();
  const positions = projectileDrawPositions(pgob) ?? [{ wx: pgob.m_wx, wy: pgob.m_wy }];
  let union: Rect | null = null;
  for (const p of positions) {
    const prc = anid.GetBounds(strip, frame);
    offsetRect(prc, ctx.coords.PcFromUwc(p.wx), ctx.coords.PcFromUwc(p.wy));
    if (union === null) {
      union = prc;
    } else {
      union.left = Math.min(union.left, prc.left);
      union.top = Math.min(union.top, prc.top);
      union.right = Math.max(union.right, prc.right);
      union.bottom = Math.max(union.bottom, prc.bottom);
    }
  }
  return union ?? empty;
}

interface AnimCaptureGob extends SimGobLike {
  GetId(): number;
  m_ani: {
    GetStrip(): number;
    GetFrame(): number;
    GetSpecialPoint?(nFrame?: number): { x: number; y: number };
  };
  m_wfUnit: number;
  m_puntc: SimUnitLike['m_puntc'];
  m_aniTurret?: { GetStrip(): number; GetFrame(): number };
  m_aniOverlay?: { GetStrip(): number; GetFrame(): number };
  m_wptFakeMiner?: { wx: number; wy: number };
  GetSide(): number;
}

/** UnitGob::GetAnimationHash (Unit.cpp:590-595) + the Tank.cpp:210/Tower.cpp:387
 *  turret xor + the Processor.cpp:200-207 overlay/fake-miner xor. The C++ mixes
 *  the OBJECT POINTER in ((frame<<16)|strip) ^ (dword)this ^ (gid<<16); a JS
 *  object has no address, and the pointer term is identity-constant per sprite —
 *  gid (already mixed via gid<<16) carries the identity, so the hash still
 *  changes exactly when frame/strip/turret/overlay state changes. */
export function gobAnimationHash(punt: AnimCaptureGob): number {
  let dw = (((punt.m_ani.GetFrame() << 16) | punt.m_ani.GetStrip()) ^ (punt.GetId() << 16)) >>> 0;
  const turret = punt.m_aniTurret as unknown as
    ({ GetStrip(): number } & { m_dir16Turret?: number }) | undefined;
  if (turret !== undefined) {
    // TankGob/TowerGob: ^= (m_dir16Turret << 8) — the turret strip IS dir16
    const dir16 =
      (punt as unknown as { m_dir16Turret?: number }).m_dir16Turret ?? punt.m_aniTurret!.GetStrip();
    dw = (dw ^ (dir16 << 8)) >>> 0;
  }
  if (punt.m_aniOverlay !== undefined) {
    // ProcessorGob: ^= fake-miner pos ^ overlay frame/strip (Processor.cpp:200-207)
    const wpt = punt.m_wptFakeMiner!;
    dw = (dw ^ (((wpt.wx << 16) | (wpt.wy & 0xffff)) >>> 0)) >>> 0;
    dw = (dw ^ (((punt.m_aniOverlay.GetFrame() << 16) | punt.m_aniOverlay.GetStrip()) >>> 0)) >>> 0;
  }
  return dw;
}

/** UnitGob::GetAnimationBounds (Unit.cpp:597-600) + the turret unions
 *  (Tank.cpp:215-226 / Tower.cpp:392-403, !fBase only) + the Processor arms
 *  (Processor.cpp:209-226: fBase = strip 0 frame 0; else union the fake miner). */
export function gobAnimationBounds(
  ctx: GobDrawContext,
  punt: AnimCaptureGob,
  fBase: boolean,
): Rect {
  const anid = ctx.anidFor(punt.m_puntc.szAniName);
  if (anid === null) return { left: 0, top: 0, right: 0, bottom: 0 };

  if (punt.m_aniOverlay !== undefined && fBase) {
    return anid.GetBounds(0, 0); // ProcessorGob fBase arm
  }
  const prc = anid.GetBounds(punt.m_ani.GetStrip(), punt.m_ani.GetFrame());
  if (!fBase && punt.m_aniTurret !== undefined && punt.m_ani.GetSpecialPoint) {
    const rcTurret = anid.GetBounds(punt.m_aniTurret.GetStrip(), punt.m_aniTurret.GetFrame());
    const pt = punt.m_ani.GetSpecialPoint();
    offsetRect(rcTurret, pt.x, pt.y);
    prc.left = Math.min(prc.left, rcTurret.left);
    prc.top = Math.min(prc.top, rcTurret.top);
    prc.right = Math.max(prc.right, rcTurret.right);
    prc.bottom = Math.max(prc.bottom, rcTurret.bottom);
  }
  const kwxInvalid = -1000;
  const kwcTile = 256;
  if (!fBase && punt.m_aniOverlay !== undefined && punt.m_wptFakeMiner!.wx !== kwxInvalid) {
    const ifrm = punt.m_wptFakeMiner!.wy < punt.m_wy + kwcTile ? 1 : 0;
    const rcMiner = anid.GetBounds(4, ifrm);
    offsetRect(
      rcMiner,
      ctx.coords.PcFromUwc(punt.m_wptFakeMiner!.wx - punt.m_wx),
      ctx.coords.PcFromWc(punt.m_wptFakeMiner!.wy - punt.m_wy),
    );
    prc.left = Math.min(prc.left, rcMiner.left);
    prc.top = Math.min(prc.top, rcMiner.top);
    prc.right = Math.max(prc.right, rcMiner.right);
    prc.bottom = Math.max(prc.bottom, rcMiner.bottom);
  }
  return prc;
}

/** UnitGob::DrawAnimation (Unit.cpp:602-605) + the turret overlays
 *  (Tank.cpp:228-236 / Tower.cpp:405-412) + the Processor overlay/fake-miner
 *  (Processor.cpp:228-252) — the hilight-sprite CAPTURE renderer. */
export function gobDrawAnimation(
  ctx: GobDrawContext,
  punt: AnimCaptureGob,
  pbm: CpuDibBitmap,
  x: number,
  y: number,
): void {
  const anid = ctx.anidFor(punt.m_puntc.szAniName);
  if (anid === null) return;
  const side = punt.GetSide();
  anid.DrawFrame(punt.m_ani.GetStrip(), punt.m_ani.GetFrame(), pbm, x, y, side);
  if (punt.m_aniTurret !== undefined && punt.m_ani.GetSpecialPoint) {
    const pt = punt.m_ani.GetSpecialPoint();
    anid.DrawFrame(
      punt.m_aniTurret.GetStrip(),
      punt.m_aniTurret.GetFrame(),
      pbm,
      x + pt.x,
      y + pt.y,
      side,
    );
  }
  if (punt.m_aniOverlay !== undefined && punt.m_ani.GetStrip() !== 2) {
    const kwxInvalid = -1000;
    const kwcTile = 256;
    if (punt.m_wptFakeMiner!.wx !== kwxInvalid) {
      const ifrm = punt.m_wptFakeMiner!.wy < punt.m_wy + kwcTile ? 1 : 0;
      anid.DrawFrame(
        4,
        ifrm,
        pbm,
        x + ctx.coords.PcFromUwc(punt.m_wptFakeMiner!.wx - punt.m_wx),
        y + ctx.coords.PcFromWc(punt.m_wptFakeMiner!.wy - punt.m_wy),
        side,
      );
    }
    anid.DrawFrame(punt.m_aniOverlay.GetStrip(), punt.m_aniOverlay.GetFrame(), pbm, x, y, side);
  }
}

/** game/Builder.cpp:357-390 BuilderGob::DrawUpgradeEffect — the galaxite-color
 *  border flash over the upgrading building (2-update phase), and the health
 *  bar when not selected. Reached from the MobileUnitBuilder depth-sorted arm
 *  (MobileBuilder.cpp:33-34) when fUpgradeInProgress is set. */
export function drawUpgradeEffect(
  ctx: GobDrawContext,
  pgob: SimUnitLike & {
    GetTilePaddedWRect(): { left: number; top: number; right: number; bottom: number };
    IsAlly(side: number): boolean;
  },
  plrLocal: { GetSide(): number; GetHandicap(): number },
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
): void {
  const kfGobActive = 0x8000; // ht.h
  const kfHcapShowEnemyBuildProgress = 0x0008;
  if (!(pgob.GetFlags() & kfGobActive)) return;
  if (
    !pgob.IsAlly(plrLocal.GetSide()) &&
    (plrLocal.GetHandicap() & kfHcapShowEnemyBuildProgress) === 0
  )
    return;

  const rcBounds = rectFromWorldRect(ctx.coords, pgob.GetTilePaddedWRect());
  offsetRect(rcBounds, -xViewOrigin, -yViewOrigin);
  const nT = Math.trunc(
    Math.min(rcBounds.right - rcBounds.left, rcBounds.bottom - rcBounds.top) / 3,
  );

  const cupd = ctx.getUpdateCount?.() ?? 0;
  const n = cupd % 4;
  const clr = GetColor(iclr.kiclrGalaxite);
  if (n < 2) {
    const rcT = { ...rcBounds };
    rcT.left += nT;
    rcT.top += nT;
    rcT.right -= nT;
    rcT.bottom -= nT; // Inflate(-nT,-nT)
    drawBorder2(pbm, rcT, clr);
  } else {
    drawBorder2(pbm, rcBounds, clr);
  }

  if (!(pgob.GetFlags() & kfGobSelected)) {
    const rcT = rectFromWorldRect(ctx.coords, pgob.m_puntc.wrcUIBounds);
    offsetRect(
      rcT,
      -xViewOrigin + ctx.coords.PcFromWc(pgob.m_wx),
      -yViewOrigin + ctx.coords.PcFromWc(pgob.m_wy),
    );
    DrawHealthIndicator(pbm, rcT, pgob.m_fxHealth, pgob.m_puntc.GetArmorStrength());
  }
}

// misc.cpp DrawBorder(pbm, prc, 2, clr) — four 2px edge fills
function drawBorder2(pbm: CpuDibBitmap, rc: Rect, clr: ReturnType<typeof GetColor>): void {
  const cxy = 2;
  pbm.Fill(rc.left, rc.top, rc.right - rc.left, cxy, clr);
  pbm.Fill(rc.left, rc.bottom - cxy, rc.right - rc.left, cxy, clr);
  pbm.Fill(rc.left, rc.top + cxy, cxy, rc.bottom - rc.top - 2 * cxy, clr);
  pbm.Fill(rc.right - cxy, rc.top + cxy, cxy, rc.bottom - rc.top - 2 * cxy, clr);
}

/** game/Research.cpp:387-400 ResearchGob::Draw's depth-sorted overlay: the
 *  upgrade progress bar while m_utTarget != kutNone (base struct draw rides the
 *  normal dispatch). */
export function drawResearchUpgradeProgress(
  ctx: GobDrawContext,
  pgob: SimUnitLike & { m_utTarget: number; m_nCreditsSpentOnUpgrade: number },
  apuntc: { nUpgradeCost?: number }[],
  pbm: CpuDibBitmap,
  xViewOrigin: number,
  yViewOrigin: number,
  nLayer: number,
): void {
  const kutNone = -1;
  if (nLayer !== knLayerDepthSorted || pgob.m_utTarget === kutNone) return;
  const rcT = rectFromWorldRect(ctx.coords, pgob.m_puntc.wrcUIBounds);
  offsetRect(
    rcT,
    -xViewOrigin + ctx.coords.PcFromWc(pgob.m_wx),
    -yViewOrigin + ctx.coords.PcFromWc(pgob.m_wy),
  );
  DrawBuildProgressIndicator(
    pbm,
    rcT,
    pgob.m_nCreditsSpentOnUpgrade,
    apuntc[pgob.m_utTarget].nUpgradeCost ?? 1,
  );
}
